import { useCallback, useEffect, useState } from 'react'
import { api } from '@/lib/api/requests'
import { consumeRoutePrefetch } from '@/lib/api/routePrefetch'
import type { SessionBalanceClub, SessionBalancesResponse } from './sessionBalance'
import { splitPassRows } from './sessionBalancePass'

// THE ONE KEY THIS READ IS CACHED UNDER: `/me/session-balances`. A press elsewhere that changed
// what it answers (a cancel on `/me` → Lessons, a pass removed or paid) invalidates it, and every
// mounted reader re-asks quietly — that key only, never a blanket page refetch.
const listeners = new Set<() => void>()

/** Every mounted `useSessionBalances` re-reads (quietly, keeping the last good list on screen). */
export function invalidateSessionBalances(): void {
  for (const listener of [...listeners]) listener()
}

export type SessionBalancesLoadState = 'loading' | 'success' | 'error'

export type UseSessionBalancesResult = {
  clubs: SessionBalanceClub[]
  loadState: SessionBalancesLoadState
  /** Re-asks this one read after a club-package press changed it (Finish payment, Remove — brick
   *  S3-FE). Quiet: the last good list stays on screen until the new round lands, same as
   *  `useMyPackageQuotes`'s own reload. A failed reload keeps that last list rather than hiding it. */
  reload: () => void
}

/**
 * The viewer's session balances, grouped by club — THE read for every surface
 * that shows the number (`docs/ux/2026-08-10-programme-balance.md` §4.0, D10).
 *
 * One hook, one endpoint. The profile block reads it today and the club's Lessons
 * tab reads it next; concurrent GETs for the same path share one request at the
 * api layer, so two consumers on one screen cost one round trip. It is deliberately
 * NOT hung off `/me/lessons` — that read is already a scan, and joining purchases
 * onto it would slow the Lessons tab for the majority who hold no balance at all.
 *
 * ERRORS HIDE THE BLOCK rather than raising an alert (D9), so there is nothing for a retry to
 * land in on the FIRST read. A balance is additive information — alarming a parent about a
 * number they may not even hold is worse than its absence. `handleAuthErrors: false` for the
 * same reason: an expired token on this additive read must not bounce the viewer to /login
 * while they are reading their lessons. `reload` (brick S3-FE) is a SEPARATE thing: a round
 * asked again only after the viewer's own press (Finish payment, Remove) changed what this read
 * answers, exactly as `useMyPackageQuotes`'s own `reload` re-asks after a buy-sheet press. A
 * reload failure keeps the last good list rather than hiding a block the viewer was just using.
 *
 * Rows that hold no balance are dropped SERVER-side (D11), so an empty `clubs`
 * means "holds nothing", never "zero left", and the block simply does not render.
 *
 * `prefetchKey` lets a route entry start the read in parallel with its lazy chunk.
 */
export function useSessionBalances({ prefetchKey }: { prefetchKey?: string } = {}): UseSessionBalancesResult {
  const [clubs, setClubs] = useState<SessionBalanceClub[]>([])
  const [loadState, setLoadState] = useState<SessionBalancesLoadState>('loading')
  const [round, setRound] = useState(0)

  useEffect(() => {
    let cancelled = false
    // Only the FIRST round may consume a route prefetch — a reload always asks fresh.
    ;((round === 0 && prefetchKey ? consumeRoutePrefetch<SessionBalancesResponse>(prefetchKey) : null) ?? fetchSessionBalances())
      .then((response) => {
        if (cancelled) return
        setClubs(Array.isArray(response?.clubs) ? response.clubs : [])
        setLoadState('success')
      })
      .catch(() => {
        if (cancelled) return
        if (round > 0) return // a reload's failure keeps the list that is already on screen
        // Includes a Lambda that predates the endpoint (404): the tab renders as
        // it did yesterday rather than reporting a feature the viewer never saw.
        setClubs([])
        setLoadState('error')
      })
    return () => {
      cancelled = true
    }
  }, [prefetchKey, round])

  const reload = useCallback(() => setRound((n) => n + 1), [])
  useEffect(() => {
    listeners.add(reload)
    return () => {
      listeners.delete(reload)
    }
  }, [reload])
  return { clubs, loadState, reload }
}

export function fetchSessionBalances(): Promise<SessionBalancesResponse> {
  return api.get<SessionBalancesResponse>('/me/session-balances', { handleAuthErrors: false }).then(splitPassRows)
}
